import { isAfter, isBefore } from 'date-fns'

import { AnonymousLearningItem } from '../../../models/AnonymousLearningItem'
import { CommentRoot, Comment } from '../../../models/Comment'
import { CurrentUser } from '../../../models/CurrentUser'
import { Group } from '../../../models/Group'
import { LearningItem } from '../../../models/LearningItem'
import { User } from '../../../models/User'
import dueDateTimestamp from '../dueDateTimestamp'
import learningItemDueDate from '../learningItemDueDate'
import { isOneOfUsers } from '../usersHelpers'
import { doesLearningItemHasTrainerOf } from '../learningItemHelpers'

export function userCanPreviewLearningItem(currentUser: CurrentUser | undefined, _learningItem: AnonymousLearningItem) {
  return currentUser?.permissions.learning_item.preview
}

export function userCanBookLearningItem(currentUser: CurrentUser | undefined, learningItem: AnonymousLearningItem) {
  return (
    currentUser?.permissions.learning_item.book &&
    !(learningItem as any).learningProgress?.completedAt &&
    learningItem.status === 'published' &&
    (!currentUser?.uuid || !doesLearningItemHasTrainerOf(learningItem, currentUser.uuid))
  )
}

export function userCanBookLearningItemAfterItHasStarted(
  currentUser: CurrentUser | undefined,
  learningItem: LearningItem
) {
  if (!userCanBookLearningItem(currentUser, learningItem)) {
    return false
  }

  return currentUser?.role === 'admin' || currentUser?.role === 'customer_success_manager'
}

export function userCanBookLearningItemBeforeDueDate(currentUser: CurrentUser | undefined, learningItem: LearningItem) {
  if (!userCanBookLearningItem(currentUser, learningItem)) {
    return false
  }
  if (learningItem.kind === 'scheduledCourse') {
    const now = new Date()
    const dueDate = learningItemDueDate(learningItem)
    if (
      dueDate &&
      !learningItem.scheduledCourse.events.some(
        ({ startDate, endDate }) => isAfter(startDate, now) && isBefore(endDate, dueDateTimestamp(dueDate))
      )
    ) {
      return false
    }
  }
  return true
}

export function userCanEditLearningItem(_currentUser: CurrentUser | undefined, learningItem: AnonymousLearningItem) {
  return learningItem.permissions.edit || learningItem.permissions.edit_description
}

export function userCanPublishLearningItem(_currentUser: CurrentUser | undefined, learningItem: AnonymousLearningItem) {
  return learningItem.permissions.edit
}

export function userCanDeprecateLearningItem(
  _currentUser: CurrentUser | undefined,
  learningItem: AnonymousLearningItem
) {
  return learningItem.permissions.edit
}

export function useCanManageLearningItemAssets(
  _currentUser: CurrentUser | undefined,
  learningItem: AnonymousLearningItem
) {
  return learningItem.permissions.assets_manage
}

export function userCanCreateScheduledCourse(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.scheduled_course_create
}

export function userCanCreateGlobalScheduledCourse(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.scheduled_course_create_global
}

export function userCanCreateOnDemandCourse(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.on_demand_course_create
}

export function userCanCreateGlobalOnDemandCourse(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.on_demand_course_create_global
}

export function userCanCreateLearningPath(currentUser: CurrentUser | undefined): boolean {
  return !!currentUser?.permissions.learning_path_create
}

/**********************************************************************
 * user
 **********************************************************************/
export function userCanCreateSomeUser(user?: CurrentUser) {
  return (
    user?.permissions.user_create_user ||
    user?.permissions.user_create_advanced ||
    user?.permissions.user_create_admin ||
    user?.permissions.user_create_auditor ||
    user?.permissions.user_create_csm
  )
}

export function userCanViewPrivateUserInformation(_currentUser: CurrentUser | undefined, user: User) {
  return user.permissions.view_private
}

export function userCanEditUserProfile(_currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && user.permissions.edit_profile
}

export function userCanEditUser(_currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && user.permissions.edit
}

export function userCanChangeUserStatus(_currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && user.permissions.change_status
}

export function userCanResetUserPassword(_currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && user.permissions.reset_password
}

export function userCanDeleteUser(_currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && user.permissions.delete
}

export function managerCanAssignLearningItemsToGroupMember(
  managerGroups: { uuid: string }[] | [],
  memberGroups: { uuid: string }[] | []
) {
  return isManagerOfGroupThatUserIsMemberOf(managerGroups, memberGroups)
}

/**
 * The unconditional permission to assign any learning item to any user
 */
export function userCanAssignLearningItems(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.user.assign_learning_item
}

export function userCanEditUserLearningAssignment(currentUser: CurrentUser | undefined) {
  return !!currentUser?.permissions.user.edit_learning_assignment
}

export function managerCanEditLearningAssignmentOfGroupMember(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  groupsThatAssignedCourseToUser: { groupUuid: string }[] | []
) {
  return isManagerInGroupThatAssignedCourseToUser(managerGroups, userGroups, groupsThatAssignedCourseToUser)
}

export function managerCanEditLearningAssignmentOfGroupMemberWhenAssignmentWasAddedDirectly(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  isDirectAssignment: boolean
) {
  const isManagerOfUser = isManagerOfGroupThatUserIsMemberOf(managerGroups, userGroups)

  return isDirectAssignment && isManagerOfUser
}

export function userCanRemoveLearningAssignment(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.user.delete_learning_assignment
}

export function managerCanRemoveLearningAssignmentFromGroupMember(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  groupsThatAssignedCourseToUser: { groupUuid: string }[] | []
) {
  return isManagerInGroupThatAssignedCourseToUser(managerGroups, userGroups, groupsThatAssignedCourseToUser)
}

export function managerCanRemoveLearningAssignmentFromGroupMemberWhenItWasAddedDirectlyByGroupManager(
  currentUserUuid: string,
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  learningItem: LearningItem
): boolean {
  let managerAssignmentUuids: string[] = []

  const isManagerOfUser = isManagerOfGroupThatUserIsMemberOf(managerGroups, userGroups)
  if (!isManagerOfUser) {
    return false
  }

  managerAssignmentUuids =
    learningItem.userLearningAssignments
      ?.filter((assignment) => assignment.assignedByUserUuid === currentUserUuid)
      .map((assignment) => assignment.uuid) || []

  return managerAssignmentUuids.length > 0
}

export function userCanMarkLearningItemAsFinished(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.user.mark_learning_item_as_finished
}

export function managerCanMarkLearningAssignmentAsFinishedForGroupMember(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  groupsThatAssignedCourseToUser: { groupUuid: string }[] | []
) {
  return isManagerInGroupThatAssignedCourseToUser(managerGroups, userGroups, groupsThatAssignedCourseToUser)
}

export function managerCanMarkLearningAssignmentAsFinishedForGroupMemberWhenAssignmentWasAddedDirectly(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  isDirectAssignment: boolean
) {
  const isManagerOfUser = isManagerOfGroupThatUserIsMemberOf(managerGroups, userGroups)

  return isDirectAssignment && isManagerOfUser
}

/**
 * The unconditional permission to cancel any booking
 */
export function userCanCancelBooking(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.user.bookings
}

export function managerCanCancelBookingOfGroupMember(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  groupsThatAssignedCourseToUser: { groupUuid: string }[] | []
) {
  return isManagerInGroupThatAssignedCourseToUser(managerGroups, userGroups, groupsThatAssignedCourseToUser)
}

export function managerCanCancelBookingOfGroupMemberWhenAssignmentWasAddedDirectly(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  isDirectAssignment: boolean
) {
  const isManagerOfUser = isManagerOfGroupThatUserIsMemberOf(managerGroups, userGroups)

  return isDirectAssignment && isManagerOfUser
}

/**********************************************************************
 * group
 **********************************************************************/
export function userCanViewPrivateGroupInformation(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.view_private
}

export function userCanViewLearningAssignmentsForGroup(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.view_learning_assignment
}

export function userCanCreateSomeGroup(user?: CurrentUser) {
  return user?.permissions.group_create
}

export function userCanEditGroup(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.edit
}

/**
 * The group has the permissions list attached
 * that the current user can perform on it.
 *
 * @param _currentUser
 * @param group
 */
export function userCanAssignSomeLearningItemToGroup(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.assign_learning_item
}

export function userCanAssignLearningItemToSomeGroup(
  currentUser: CurrentUser | undefined,
  learningItem: AnonymousLearningItem
) {
  return currentUser?.permissions.group.assign_learning_item && learningItem.status === 'published'
}

export function userCanEditGroupLearningAssignment(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.edit_learning_assignment
}

export function userCanRemoveGroupLearningAssignment(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.delete_learning_assignment
}

/**********************************************************************
 * user / group
 **********************************************************************/
export function userCanAddUserToSomeGroupAsMember(currentUser: CurrentUser | undefined, user: User) {
  return !user.deletedAt && currentUser?.permissions.group.add_member
}

export function userCanRemoveUserFromGroupAsMember(_currentUser: CurrentUser | undefined, _user: User, group: Group) {
  return group.permissions.remove_member
}

export function userCanRemoveUserFromGroupAsGroupManager(
  _currentUser: CurrentUser | undefined,
  _user: User,
  group: Group
) {
  return group.permissions.remove_manager
}

export function userCanAddUserToGroupAsMember(currentUser: CurrentUser | undefined, user: User, group: Group) {
  return !user.deletedAt && group.permissions.add_member
}

export function userCanAddUserToGroupAsGroupManager(_currentUser: CurrentUser | undefined, user: User, group: Group) {
  return !user.deletedAt && group.permissions.add_manager
}

export function userCanAddSomeUserToGroupAsGroupManager(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.add_manager
}

export function userCanAddSomeUserToGroupAsMember(_currentUser: CurrentUser | undefined, group: Group) {
  return group.permissions.add_member
}

/**********************************************************************
 * category
 **********************************************************************/
export function userCanCreateCategory(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.category_create
}

/**********************************************************************
 * provider
 **********************************************************************/
export function userCanCreateProvider(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.provider_create
}

export function userCanCreateGlobalProvider(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.provider_create_global
}

/**********************************************************************
 * trainer
 **********************************************************************/
export function userCanCreateTrainer(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.trainer_create
}

export function userCanCreateGlobalTrainer(currentUser: CurrentUser | undefined) {
  return currentUser?.permissions.trainer_create_global
}

/**********************************************************************
 * comment
 **********************************************************************/
export function userCanCommentOnRoot(_currentUser: CurrentUser | undefined, root: CommentRoot) {
  return root.permissions.comment
}

export function userCanEditCommentOnRoot(currentUser: CurrentUser | undefined, comment: Comment, _root: CommentRoot) {
  return comment.userUuid === currentUser?.uuid
}

export function userCanDeleteCommentOnRoot(currentUser: CurrentUser | undefined, comment: Comment, root: CommentRoot) {
  return comment.userUuid === currentUser?.uuid || root.permissions.comment_delete_permanent
}

export function userCanDeleteCommentOnRootPermanently(
  _currentUser: CurrentUser | undefined,
  _comment: Comment,
  root: CommentRoot
) {
  return root.permissions.comment_delete_permanent
}

export function userCanUpdateAttendance(user: { role: string; uuid: string }, trainers: { uuid: string }[]): boolean {
  return ['admin', 'customer_success_manager'].includes(user.role) || isOneOfUsers(user.uuid, trainers)
}

export function userCanAccessEventDateAttendeesList(
  user: { role: string; uuid: string },
  trainers: { uuid: string }[]
): boolean {
  return ['admin', 'customer_success_manager'].includes(user.role) || isOneOfUsers(user.uuid, trainers)
}

function isManagerOfGroupThatUserIsMemberOf(
  managerManagedGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | []
): boolean {
  if (managerManagedGroups.length === 0 || userGroups.length === 0) {
    return false
  }

  const managerGroups = managerManagedGroups.map((group) => group.uuid)
  if (managerGroups.length === 0) {
    return false
  }

  let managerGroupsUserBelongsTo = userGroups.map((group) => {
    return managerGroups.includes(group.uuid) ? group.uuid : null
  })

  managerGroupsUserBelongsTo = managerGroupsUserBelongsTo.filter((uuid) => uuid !== null)

  return managerGroupsUserBelongsTo.length > 0
}

function isManagerInGroupThatAssignedCourseToUser(
  managerGroups: { uuid: string }[] | [],
  userGroups: { uuid: string }[] | [],
  groupsThatAssignedCourseToUser: { groupUuid: string }[] | []
): boolean {
  if (managerGroups.length === 0 || userGroups.length === 0 || groupsThatAssignedCourseToUser.length === 0) {
    return false
  }

  const managedGroups = managerGroups.map((group) => group.uuid)
  if (managedGroups.length === 0) {
    return false
  }

  const managedGroupsOtherUserBelongsTo = userGroups.map((group) => {
    return managedGroups.includes(group.uuid) ? group.uuid : null
  })
  if (managedGroupsOtherUserBelongsTo.length === 0) {
    return false
  }

  let managedGroupsWithCourseAssignedOtherUserBelongsTo = groupsThatAssignedCourseToUser.map((assignment) => {
    return managedGroupsOtherUserBelongsTo.includes(assignment.groupUuid) ? assignment.groupUuid : null
  })

  managedGroupsWithCourseAssignedOtherUserBelongsTo = managedGroupsWithCourseAssignedOtherUserBelongsTo.filter(
    (uuid) => uuid !== null
  )

  return managedGroupsWithCourseAssignedOtherUserBelongsTo.length > 0
}
